// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';

import type { TourDef, TourStepDef, ToursSource } from '@ergoss/guided-tour';
import { LoggerService } from '@core/services/logger.service';
import { unwrap } from 'src/app/api/unwrap';

/** App-tour `view` for the flight-detail section. Unlike the four main views
 *  (whose strings equal the tab names), flight-detail is an OVERLAY, not a
 *  route — the shell reacts to this value by opening the first flight over the
 *  Flights tab. Lives HERE (app content/policy, not the tour engine's model —
 *  the engine treats `view` as an opaque string) and is shared by the tour
 *  data below and the shell so the two can't drift. */
export const TOUR_FLIGHT_DETAILS_VIEW = 'flight-details';
/** App-tour view for the loaded-briefing section: the shell submits a real
 *  briefing (placeholder route — the pilot's most recent flight) before these
 *  steps show, and resets it on the way out. Mirrors the flight-details view. */
export const TOUR_BRIEFING_DETAILS_VIEW = 'briefing-details';

/**
 * Tour content = LBO's mockup texts and storyline (SARAPWI.Front `ea1abc3` +
 * `6ebb78c` + `1fe3b58`), kept verbatim per the PM's brief ("garder son texte
 * et l'enchainement de la storyline").
 *
 * The copy lives ONCE, in per-section step arrays. From those we compose both:
 *   - the SINGLE app-wide tour (`APP_TOUR`, `navigates: true`) — LBO's
 *     cross-page storyline: intro → Home → Flights → Flight detail → Briefing →
 *     Reports → farewell, exactly like his `APP_TOUR_STEPS = [...sections]`.
 *     Each step carries its `view`; the shell navigates there before it shows.
 *   - the per-page tours (Home, Flights, Flight detail, Briefing, …) that the
 *     top-bar "?" button replays for the current screen.
 * Anchor-less steps render as centred cards.
 *
 * DEVIATION from the mock: his app tour walks the LOADED briefing (map, NOTAMs,
 * …) via a `?demo=1` route that auto-loads a canonical LFPO→LGAV briefing — a
 * seam we don't have (our briefing is real-backend, two exclusive states). So
 * the app tour walks the briefing FORM only; the loaded-briefing walkthrough
 * stays on the `briefing-details` per-page "?" tour.
 */

// ── App-level intro (welcome + the chrome shared by every screen) ────────────
const APP_INTRO_STEPS: TourStepDef[] = [
  {
    title: 'Welcome to SARA PWI',
    body: 'This tour walks you through every section of the app: Home, Flights, Flight detail, Briefing, and Reports. You can skip any time, or restart from the "?" button in the top bar.',
  },
  {
    anchor: 'topbar.fleet-selector',
    title: 'Fleet selector',
    body: "Switch between the fleets you've flown with. Every screen filters its data to the active fleet.",
  },
  {
    anchor: 'nav.tabs',
    title: 'Main navigation',
    body: "Four sections: Home for your global overview, Flights to browse the flights you've flown, Briefing to prep an upcoming flight, and Reports for pilot and fleet insights.",
  },
];

// ── Home ─────────────────────────────────────────────────────────────────────
const HOME_INTRO_STEP: TourStepDef = {
  title: 'Home dashboard',
  body: 'Your global overview at a glance — the latest airline message, flights waiting on your review, key operations stats, and your most-flown airports and routes.',
};

const HOME_CARD_STEPS: TourStepDef[] = [
  {
    anchor: 'home.airline-message',
    title: 'Airline message',
    body: 'The card displays the latest message from your airline. Tap the card to expand and read the full content.',
  },
  {
    anchor: 'home.flights-to-review',
    title: 'Flights to review',
    body: 'Flights that are waiting on your pilot review. Tap one to open its detail and review it.',
  },
  {
    anchor: 'home.overview',
    title: 'Overview',
    body: 'Key operations metrics — switch categories (safety, operations, …) and change the period to explore trends. Pick which metrics appear here from the user settings.',
  },
  {
    anchor: 'home.top-airports',
    title: 'Top Airports & Routes',
    body: 'Your most-flown airports and routes for the selected period.',
  },
];

// ── Flights ────────────────────────────────────────────────────────────────
const FLIGHTS_STEPS: TourStepDef[] = [
  {
    title: 'Flights tour',
    body: "Every flight you've flown, grouped by month. Each card shows event and comment counts at a glance. Tap a card to open its detail.",
  },
  {
    anchor: 'topbar.flight-scope',
    title: 'Scope tabs',
    body: "Quick filters: All flights, only those needing review, or the flights you've bookmarked.",
  },
  {
    anchor: 'topbar.flight-filters',
    title: 'Filters',
    body: 'Narrow the list by month, flights with events, flights with unread comments, airport pair (origin / destination), or aircraft registration.',
  },
  {
    anchor: 'topbar.flight-layout',
    title: 'Layout',
    body: 'Grid or split: in split view the flights stack in a column and tapping one previews it right here, beside the list. Open in full page from the preview when you need the whole screen.',
    // The toggle only renders with the FlightsSplitView flag on, in landscape,
    // on a screen wide enough for the split — absent otherwise, so the step
    // must be allowed to skip (same overlay as the data-gated detail panels).
    optional: true,
  },
  {
    anchor: 'flights.list',
    title: 'Grouped list',
    body: 'Flights are grouped by month for easy scanning. Use the month picker to jump quickly to a specific period.',
  },
];

// ── Flight detail (LBO's second push, `1fe3b58`) ─────────────────────────────
// The panels behind `optional` are gated on the flight's data — a restricted
// flight (no data available) renders no replay button, no general-info card and
// none of the events / FOI / comments / snapshots panels, so those anchors never
// mount. Without the flag the app-wide tour narrates them as centred cards
// describing something that is not on screen. Requires @ergoss/guided-tour
// >= 0.1.0-dev.9. `header` and `map` stay required — they render for any flight.
const FLIGHT_DETAILS_STEPS: TourStepDef[] = [
  {
    title: 'Flight detail',
    body: 'The full picture of a single flight — route, weather, crew, events, comments, and snapshots — all on one screen.',
  },
  {
    anchor: 'flight-details.header',
    title: 'Flight identity',
    body: 'Flight number, aircraft registration, and aircraft type. Tap the bookmark to save this flight to your Bookmarks so you can find it back quickly.',
  },
  {
    anchor: 'flight-details.map',
    title: 'Route map & weather',
    body: 'The actual flown route with a METAR summary card per airport. Tap a card to expand it and see the full METAR for that airport.',
  },
  {
    anchor: 'flight-details.replay',
    optional: true,
    title: 'Flight replay',
    body: 'Relive the flight in an interactive replay — PFD, MFD, 3D, 2D, and more views play back in sync.',
  },
  {
    anchor: 'flight-details.general-info',
    optional: true,
    title: 'General information',
    body: 'Check the crew (tap a chip to reveal initials) on this flight, and confirm who was Pilot Flying at take-off and at landing. If you change a PF, a Submit button appears here to lock it in.',
  },
  {
    anchor: 'flight-details.crew-error',
    optional: true,
    title: 'Report a crew mismatch',
    body: 'Spotted a wrong crew assignment? Use this button to flag it — the report goes to ops and admins for correction.',
  },
  {
    anchor: 'flight-details.events',
    optional: true,
    title: 'Events',
    body: 'Events triggered during the flight, colour-coded by severity. Add a comment to any event or click on the play button to jump to the replay at that moment.',
  },
  {
    anchor: 'flight-details.foi',
    optional: true,
    title: 'Flight ops indicators',
    body: 'The badge counts how many flight ops indicators triggered on this flight. Open it to see the details.',
  },
  {
    anchor: 'flight-details.comments',
    optional: true,
    title: 'Comments',
    body: 'Every comment tied to this flight — from FOQA analysts and from crew. Switch the FOQA / FOQA + Crew tab to filter, and reply inline. You can also add comments related to flagged events, which appear as chips above the input box.',
  },
  {
    anchor: 'flight-details.snapshots',
    optional: true,
    title: 'Snapshots',
    body: 'Instantaneous readings for the flight sorted by category. Each arc gauge shows how the parameter compares against the fleet distribution — green (-1σ / +1σ) → yellow (-3σ / -1σ, +1σ / +3σ) → red (< -3σ / > +3σ).',
  },
];

// ── Briefing — form (empty state) + loaded-state details ─────────────────────
const BRIEFING_FORM_STEPS: TourStepDef[] = [
  {
    title: 'Briefing tour',
    body: 'The briefing page bundles everything you need to prepare a flight: route form, weather, NOTAMs, previous flights on the same route, admin notices, and interactive reports.',
  },
  {
    anchor: 'briefing.route-form',
    title: 'Route form',
    body: 'Fill in the departure and arrival airports (ICAO codes). You can add an alternate and set the takeoff / landing times to fetch the correct METAR/TAF window.',
  },
];

// Loaded-briefing walkthrough — its own seen-state; the form tour is marked
// seen after 2 steps so these detail steps would never play if merged. Reached
// via the "?" once a briefing is loaded (top-bar `currentTourId`). NOT in the
// app tour (no demo-briefing auto-load seam — see the deviation note above).
const BRIEFING_DETAILS_STEPS: TourStepDef[] = [
  {
    anchor: 'briefing.map',
    title: 'Route map',
    body: 'See your route on the map, with a METAR/TAF summary card for each airport.',
  },
  {
    anchor: 'briefing.sample-flights',
    title: 'Sample flights',
    body: 'Pull up sample flights for this route — real past flights you can inspect to see how similar operations played out (takeoff, landing, or the full route).',
  },
  {
    anchor: 'briefing.notices',
    title: 'Notices',
    body: 'Admin messages relevant to the selected airports or dates — colour-coded by severity.',
  },
  {
    anchor: 'briefing.previous-flights',
    title: 'Previous flights',
    body: 'Your prior flights that share the selected route (or one of the airports) — handy to spot how you flew this leg last time. Tap a card to open its detail.',
  },
  {
    anchor: 'briefing.notams',
    title: 'NOTAMs',
    body: 'NOTAMs for the selected airports. Use the Filters pill to narrow by keyword (ILS, Runway, Closed, …) — critical items are highlighted in red.',
  },
  {
    anchor: 'briefing.report-card',
    title: 'Interactive reports',
    body: 'Analytics for this route — FOQA indicators, snapshots, trajectories, and more.',
  },
];

// ── Reports ──────────────────────────────────────────────────────────────────
const REPORTS_STEPS: TourStepDef[] = [
  {
    title: 'Reports tour',
    body: 'Pilot and fleet insights — pick a report, tune its filters, and read the results. Every report is scoped to the active fleet at the top-left.',
  },
  {
    anchor: 'topbar.report-picker',
    title: 'Report picker',
    body: 'Tap the report name to open the picker. Reports are organised into categories (Pilot, Fleet, …).',
  },
  {
    anchor: 'topbar.report-filters',
    title: 'Filters',
    body: 'Each report has its own filter set — period, time-of-day bucket, event severity, and more. An active-filter badge on the button tells you when a filter is in play.',
  },
  {
    anchor: 'reports.viewer',
    title: 'Report content',
    body: 'The chosen report renders here. Charts and tables are interactive — hover, click, and scroll to drill into the data.',
  },
];

const FINISH_STEP: TourStepDef = {
  title: 'You are all set',
  body: 'You can restart this tour any time from the "?" button in the top bar, and every screen has its own "?" for a focused walkthrough.',
};

/** Tags each step of a section with the view it lives on — the shell reads it
 *  to navigate the app tour to the right screen before the step shows. */
const withView = (view: string, steps: TourStepDef[]): TourStepDef[] => steps.map((step) => ({ ...step, view }));

/** Briefing-details steps as the APP tour walks them: tagged with the view the
 *  shell drives (load a placeholder-route briefing) and `optional` — the cards
 *  cull instead of narrating an empty page if that load fails or returns
 *  nothing. Used for the local catalog AND to splice the SERVED per-page def
 *  into the served app def (see spliceBriefingDetails). */
const asAppBriefingDetails = (steps: TourStepDef[]): TourStepDef[] =>
  steps.map((step) => ({ ...step, view: TOUR_BRIEFING_DETAILS_VIEW, optional: true }));

// ── Per-page tours — replayed by the top-bar "?" for the current screen ──────
const HOME_TOUR: TourDef = { id: 'home', version: 1, steps: [HOME_INTRO_STEP, ...HOME_CARD_STEPS] };
const FLIGHTS_TOUR: TourDef = { id: 'flights', version: 1, steps: FLIGHTS_STEPS };
const FLIGHT_DETAILS_TOUR: TourDef = { id: 'flight-details', version: 1, steps: FLIGHT_DETAILS_STEPS };
const BRIEFING_TOUR: TourDef = { id: 'briefing', version: 1, steps: BRIEFING_FORM_STEPS };
const BRIEFING_DETAILS_TOUR: TourDef = { id: 'briefing-details', version: 1, steps: BRIEFING_DETAILS_STEPS };
const REPORTS_TOUR: TourDef = { id: 'reports', version: 1, steps: REPORTS_STEPS };

// ── The single app-wide tour — auto-runs once on first visit ─────────────────
// The sections concatenated in LBO's storyline order, each step tagged with its
// view. `flight-details` steps make the shell open the first flight; the
// farewell has no view (it stays on Reports).
const APP_TOUR: TourDef = {
  id: 'app',
  version: 1,
  navigates: true,
  steps: [
    ...withView('home', APP_INTRO_STEPS),
    ...withView('home', [HOME_INTRO_STEP, ...HOME_CARD_STEPS]),
    ...withView('flights', FLIGHTS_STEPS),
    ...withView(TOUR_FLIGHT_DETAILS_VIEW, FLIGHT_DETAILS_STEPS),
    ...withView('briefing', BRIEFING_FORM_STEPS),
    ...asAppBriefingDetails(BRIEFING_DETAILS_STEPS),
    ...withView('reports', REPORTS_STEPS),
    FINISH_STEP,
  ],
};

/** The locally-authored catalog. With the tours backend live (PwiApi
 *  `Modules/Tours`, _CONFIG store) this is no longer what pilots are served —
 *  it is (a) the SEED SOURCE the dev-panel "Publish tours" action PUTs through
 *  the admin endpoint (HDA's migration note: content is front-owned, seed from
 *  here rather than hand-transcribing to SQL), and (b) the transitional
 *  fallback below while an environment's store is unmigrated/unseeded. Remove
 *  the fallback (not the catalog) once every target env verifiably serves the
 *  tours. */
export const LOCAL_TOURS: TourDef[] = [
  APP_TOUR,
  HOME_TOUR,
  FLIGHTS_TOUR,
  FLIGHT_DETAILS_TOUR,
  BRIEFING_TOUR,
  BRIEFING_DETAILS_TOUR,
  REPORTS_TOUR,
];

/**
 * Overlays the LOCAL catalog's `optional` flags onto backend-served tours,
 * matched by tour id + step anchor.
 *
 * Whether a flight-details panel exists for a SPECIFIC flight is decided by
 * this app's templates (`panelAccess()`, `isDataAvailable`, crew-match) — the
 * backend cannot know it, so the "this step's element may legitimately be
 * absent" marker is FRONT knowledge and ships with the app build, right next
 * to the `@if` gates it describes. Content (copy, order, which tours exist)
 * stays backend-owned; only this behavioural flag is overlaid. Steps the local
 * catalog doesn't know (studio-authored) get no flag → required → the engine's
 * old behaviour. Also makes the flag independent of the backend's step schema:
 * a DTO that strips unknown fields on PUT/GET cannot lose it.
 */
export const overlayOptionalFlags = (tours: TourDef[]): TourDef[] =>
  tours.map((tour) => {
    const local = LOCAL_TOURS.find((t) => t.id === tour.id);
    if (!local) return tour;
    const flagged = new Set(local.steps.filter((s) => s.optional && s.anchor).map((s) => s.anchor));
    if (flagged.size === 0) return tour;
    return {
      ...tour,
      steps: tour.steps.map((s) => (s.anchor && flagged.has(s.anchor) ? { ...s, optional: true } : s)),
    };
  });

/**
 * Splices the SERVED `briefing-details` tour into the SERVED app tour, after
 * the briefing-form section — the app-tour storyline includes the loaded-
 * briefing walkthrough (LBO's mock authored it; the lib port lost it), but the
 * backend defs predate that and the engine walks one def's own list.
 *
 * Like the `optional` overlay below: card CONTENT stays backend-owned (the
 * spliced titles/bodies come from the served per-page def, so a studio edit
 * flows into the app walk), while COMPOSITION — which sections the storyline
 * drives and how — ships with the app build, next to the shell code that
 * drives it. No re-seed needed on any environment; once a future seed pushes
 * the composed app def, the duplicate guard makes this a no-op.
 */
export const spliceBriefingDetails = (tours: TourDef[]): TourDef[] => {
  const app = tours.find((t) => t.navigates);
  const details = tours.find((t) => t.id === 'briefing-details');
  if (!app || !details || details.steps.length === 0) return tours;
  if (app.steps.some((s) => s.view === TOUR_BRIEFING_DETAILS_VIEW)) return tours; // already composed
  const lastBriefing = app.steps.map((s) => s.view).lastIndexOf('briefing');
  if (lastBriefing === -1) return tours; // no briefing section — nowhere coherent to splice
  const steps = [
    ...app.steps.slice(0, lastBriefing + 1),
    ...asAppBriefingDetails(details.steps),
    ...app.steps.slice(lastBriefing + 1),
  ];
  return tours.map((t) => (t === app ? { ...t, steps } : t));
};

/** Outcome of a dev-panel publish: how many defs were PUT, and how many tours
 *  the backend actually serves on the verify read — `served: 0` after a
 *  successful publish exposes the Null-repo 200-void (unwired _CONFIG). */
export interface TourPublishResult {
  published: number;
  served: number;
}

/**
 * Source of the guided-tour definitions — the backend `GET /api/pwi/tours`
 * (PwiApi Tours module, shared _CONFIG store), plain HttpClient + `unwrap`
 * (fetchReportApi precedent; regen can ride a later client batch). The future
 * tour studio writes this same TourDef shape via the admin endpoint.
 *
 * Fail-open on empty/error: an unseeded or unreachable store serves the local
 * catalog instead, so internal QA keeps its tours whatever the backend state.
 * Once the backend serves ANY tours, it wins outright (partial sets are its
 * truth, not merged).
 */
@Injectable({ providedIn: 'root' })
export class ToursService implements ToursSource {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  /** What the engine is actually running on — surfaced in the dev panel
   *  because the fail-open fallback is deliberately invisible in the UI
   *  (same tours either way while the backend mirrors the local catalog).
   *  Same service-owned-status idiom as NetworkService.isOnline. */
  readonly contentSource = signal('loading…');

  getTours(): Observable<TourDef[]> {
    return this.http.get<{ data?: TourDef[] | null }>('/api/pwi/tours').pipe(
      map(unwrap),
      map((tours) => {
        if (tours.length > 0) {
          this.contentSource.set(`backend (${tours.length} tours)`);
          return overlayOptionalFlags(spliceBriefingDetails(tours));
        }
        this.contentSource.set('local catalog — backend store is empty');
        return LOCAL_TOURS;
      }),
      catchError((error: unknown) => {
        // Fail-quiet like the engine: tours must never disturb the page.
        this.logger.error('tours', 'GET /tours failed — serving the local catalog', error);
        this.contentSource.set('local catalog — backend unreachable');
        return of(LOCAL_TOURS);
      }),
    );
  }

  /**
   * Dev-panel seeding + environment diagnostic: PUTs every local def through
   * the admin endpoint, then re-reads `GET /tours` to report what the backend
   * genuinely serves. Distinguishes the three backend states that all look
   * like "no tours" from the front: seeded (served = published), unwired
   * _CONFIG whose Null repo answers 200 to writes it drops (served = 0), and
   * missing migration / no admin role (the PUT errors). Errors propagate to
   * the caller's toast.
   */
  publishTours(): Observable<TourPublishResult> {
    return forkJoin(LOCAL_TOURS.map((tour) => this.http.put(`/api/pwi/admin/tours/${tour.id}`, tour))).pipe(
      switchMap(() => this.http.get<{ data?: TourDef[] | null }>('/api/pwi/tours')),
      map(unwrap),
      map((served) => ({ published: LOCAL_TOURS.length, served: served.length })),
    );
  }

  /**
   * The studio's Save path: PUTs ONE authored def through the admin endpoint,
   * then verify-reads `GET /tours` — `persisted` is whether the backend now
   * serves that exact id+version (false = the _CONFIG 200-void, same trap the
   * seeding diagnostic exposes). Errors propagate to the caller's toast.
   */
  publishTour(tour: TourDef): Observable<{ persisted: boolean }> {
    return this.http.put(`/api/pwi/admin/tours/${encodeURIComponent(tour.id)}`, tour).pipe(
      switchMap(() => this.http.get<{ data?: TourDef[] | null }>('/api/pwi/tours')),
      map(unwrap),
      map((served) => ({ persisted: served.some((t) => t.id === tour.id && t.version === tour.version) })),
    );
  }
}
